import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// Revolut-style animations
export const holographicShift = keyframes`
  0% { 
    background-position: 0% 50%;
    transform: rotateX(0deg) rotateY(0deg);
  }
  25% { 
    background-position: 100% 50%;
    transform: rotateX(5deg) rotateY(-2deg);
  }
  50% { 
    background-position: 100% 100%;
    transform: rotateX(-2deg) rotateY(3deg);
  }
  75% { 
    background-position: 0% 100%;
    transform: rotateX(3deg) rotateY(1deg);
  }
  100% { 
    background-position: 0% 50%;
    transform: rotateX(0deg) rotateY(0deg);
  }
`;

export const shimmerEffect = keyframes`
  0% { transform: translateX(-100%) skewX(-15deg); }
  100% { transform: translateX(200%) skewX(-15deg); }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-10px) rotate(1deg); }
  66% { transform: translateY(5px) rotate(-1deg); }
`;

export const StyledCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin: 24px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin: 16px 0;
  }
`;

export const StyledCreditCard = styled(motion.div)<{
  $gradient: string;
  $isSelected: boolean;
}>`
  position: relative;
  background: ${({ $gradient }) => $gradient};
  background-size: 400% 400%;
  animation: ${holographicShift} 10s ease-in-out infinite;
  border-radius: 24px;
  padding: 24px;
  min-height: 220px;
  cursor: pointer;
  border: 2px solid ${({ $isSelected }) => $isSelected ? '#fff' : 'transparent'};
  box-shadow: 
    0 10px 40px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset,
    ${({ $isSelected }) => $isSelected ? '0 0 0 3px rgba(99, 102, 241, 0.3)' : 'none'};
  backdrop-filter: blur(20px);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: ${shimmerEffect} 3s infinite;
    animation-delay: ${Math.random() * 2}s;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 20px;
    min-height: 200px;
    border-radius: 20px;
  }
`;

export const StyledCardContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
`;

export const StyledCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

export const StyledCardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

export const StyledCardType = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

export const StyledCardNumber = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 16px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 16px;
    letter-spacing: 1px;
    margin: 12px 0;
  }
`;

export const StyledCardFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

export const StyledBalance = styled.div`
  .label {
    font-size: 11px;
    font-weight: 500;
    opacity: 0.8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
  
  .amount {
    font-size: 24px;
    font-weight: 700;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    
    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
`;

export const StyledLimit = styled.div`
  text-align: right;
  
  .label {
    font-size: 10px;
    font-weight: 500;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
  
  .amount {
    font-size: 14px;
    font-weight: 600;
    opacity: 0.9;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

export const StyledCardDetails = styled(motion.div)`
  background: ${({ theme }) => theme.colors.cardBackground || '#ffffff'};
  border-radius: 20px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 20px;
    margin-top: 20px;
    border-radius: 16px;
  }
`;

export const StyledStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 20px;
  }
`;

export const StyledStatCard = styled(motion.div)`
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.12),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

export const StyledStatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const StyledStatIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px ${({ $color }) => $color}40;
  
  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const StyledStatLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary || '#6b7280'};
`;

export const StyledStatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary || '#1f2937'};
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const StyledProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

export const StyledProgress = styled(motion.div)<{ $color: string }>`
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
`;

export const StyledUtilizationText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary || '#6b7280'};
  margin-top: 4px;
  text-align: right;
`;
